/**
 * @fileoverview Infinite scrolling text marquee component with customizable speed and spacing.
 * Creates seamless horizontal text scrolling effect using Motion/React animations.
 */

import { FC, useEffect, useRef, useState } from 'react';
import { motion, Variants } from 'motion/react';
import type { MarqueeProps } from '../types/components';

/**
 * Infinite text marquee component with smooth horizontal scrolling.
 *
 * @description Creates a continuously scrolling text marquee using two identical text blocks
 * to ensure seamless looping. Features customizable scrolling speed, text spacing,
 * and responsive design. Commonly used for announcements, ticker text, or decorative elements.
 *
 * @param {MarqueeProps} props - Component properties
 * @param {string} props.text - Text content to scroll
 * @param {string} [props.className=''] - Additional CSS classes for container styling
 * @param {number} [props.duration=120] - Animation duration in seconds (higher = slower)
 * @param {'small' | 'medium' | 'large' | 'xl'} [props.separatorSpacing='large'] - Space between text repetitions
 *
 * @returns {JSX.Element} Animated marquee component
 *
 * @example
 * ```tsx
 * // Basic marquee
 * <Marquee text="Welcome to Ghost Cvlt" />
 *
 * // Fast marquee with tight spacing
 * <Marquee
 *   text="Breaking News"
 *   duration={30}
 *   separatorSpacing="small"
 *   className="bg-red-600 text-white py-2"
 * />
 *
 * // Slow, elegant marquee
 * <Marquee
 *   text="Premium Services Available"
 *   duration={180}
 *   separatorSpacing="xl"
 *   className="text-gray-400 uppercase tracking-wider"
 * />
 * ```
 */
const Marquee: FC<MarqueeProps> = ({
  text,
  className = '',
  duration = 120,
  separatorSpacing = 'large',
}) => {
  /** Measured width of text content for animation calculations */
  const [textWidth, setTextWidth] = useState<number>(0);

  /** Number of text repetitions to ensure seamless scrolling */
  const repeatTimes = 50;

  /** Maps spacing options to Tailwind CSS margin classes */
  const spacingMap = {
    small: 'mr-8', // 2rem spacing
    medium: 'mr-16', // 4rem spacing
    large: 'mr-24', // 6rem spacing
    xl: 'mr-32', // 8rem spacing
  };

  /** Reference to measure text container width */
  const textRef = useRef<HTMLDivElement | null>(null);

  /**
   * Effect to measure text width when component mounts.
   * This measurement is used to calculate smooth animation distance.
   */
  useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, [textRef]);

  /** Animation variants for smooth horizontal scrolling */
  const tickerVariants: Variants = {
    animate: {
      x: [0, -textWidth], // Move from 0 to negative text width
      transition: {
        x: {
          duration: duration,
          repeat: Infinity,
          repeatType: 'loop' as const,
          ease: 'linear', // Consistent speed throughout
        },
      },
    },
  };

  return (
    <div
      className={`overflow-hidden whitespace-nowrap w-full flex ${className}`}
    >
      {/* First text block - provides seamless scrolling reference */}
      <motion.div
        className="inline-block"
        variants={tickerVariants}
        initial="animate"
        animate="animate"
        ref={textRef}
      >
        {Array(repeatTimes)
          .fill(null)
          .map((_, index) => (
            <span key={index} className={spacingMap[separatorSpacing]}>
              {text}
            </span>
          ))}
      </motion.div>

      {/* Second text block - ensures continuous loop without gaps */}
      <motion.div
        className="inline-block"
        variants={tickerVariants}
        initial="animate"
        animate="animate"
      >
        {Array(repeatTimes)
          .fill(null)
          .map((_, index) => (
            <span key={index} className={spacingMap[separatorSpacing]}>
              {text}
            </span>
          ))}
      </motion.div>
    </div>
  );
};

export default Marquee;
